import React, { useCallback, useEffect, useState } from 'react';
import {
  edrService,
  CompanyOption,
  PolicyStatusRow,
} from '../../../services/edr.service';
import { formatPeriod } from '../../../lib/edrPeriod';
import { blockReviewer } from './edrAdminGuard';
import WriteCompleteBar from '../../report/WriteCompleteBar';

/**
 * PRD [S7] 관리자 — EDR 운영보고서 관리
 *
 * ⚠️ 이 화면은 정책값의 **출처**를 표시한다 — 저장된 값인지, 직전 기간에서 승계된 값인지.
 * 운영보고서 화면은 반대로 표시하지 않는다(결과만 보여주는 화면이므로).
 * 승계 표시가 없으면 담당자가 "이번 달에 점검한 값"과 "작년 값이 그대로 넘어온 것"을
 * 구분할 수 없다.
 *
 * 저장 버튼이 3개인 것도 의도다. `saveSummary`는 **보낸 필드만** 갱신하므로
 * 종합의견 저장이 정책 변경 이력을 지우지 않는다.
 */

const POLICY_GROUPS: Array<{ key: PolicyStatusRow['policyGroup']; title: string }> = [
  { key: 'EPP', title: 'EPP 정책' },
  { key: 'V3', title: 'V3 정책' },
  { key: 'EDR', title: 'EDR 정책' },
];

const EdrReportAdmin: React.FC<{ isReviewer: boolean }> = ({ isReviewer }) => {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [company, setCompany] = useState('');
  const [period, setPeriod] = useState('');

  const [summaryText, setSummaryText] = useState('');
  const [policyHistory, setPolicyHistory] = useState('');
  const [policies, setPolicies] = useState<PolicyStatusRow[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  /**
   * 기간 목록은 **서버가 준다** — 보고서를 만들 수 있는 달만 골라야 한다.
   *
   * 최근 12개월을 나열하면 라이선스 수집도 안 된 달에 종합의견을 쓰게 되고,
   * 그 달은 조회 화면 목록에 없어 담당자가 작성한 내용을 볼 수 없다.
   */
  const [periods, setPeriods] = useState<string[]>([]);
  const [isPeriodsLoaded, setIsPeriodsLoaded] = useState(false);
  const isReady = Boolean(period && company);

  useEffect(() => {
    edrService
      .getCompanies()
      .then(setCompanies)
      .catch(() => setMessage({ type: 'error', text: '회사 목록을 불러오지 못했습니다.' }));

    edrService
      .getAvailablePeriods()
      .then(setPeriods)
      .catch(() =>
        setMessage({ type: 'error', text: '조회 가능한 기간을 불러오지 못했습니다.' }),
      )
      .finally(() => setIsPeriodsLoaded(true));
  }, []);

  /**
   * `keepMessage`는 저장 직후 재조회용이다.
   *
   * 저장 후 재조회를 하지 않으면 승계 배지가 "저장됨"으로 갱신되지 않는다.
   * 그렇다고 재조회가 성공 메시지를 지우면 담당자는 저장됐는지 알 수 없다.
   */
  const load = useCallback(async (p: string, c: string, keepMessage = false) => {
    setIsLoading(true);
    try {
      const [summary, policyRows] = await Promise.all([
        edrService.getSummary(p, c),
        edrService.getPolicyStatus(p, c),
      ]);
      setSummaryText(summary.summaryText ?? '');
      setPolicyHistory(summary.policyHistory ?? '');
      setPolicies(policyRows);
      if (!keepMessage) setMessage(null);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || '보고서 데이터를 불러오지 못했습니다.',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isReady) return;
    load(period, company);
  }, [isReady, period, company, load]);

  const setAllApplied = (isApplied: boolean) =>
    setPolicies((prev) => prev.map((p) => ({ ...p, isApplied })));

  const togglePolicy = (policyCode: string) =>
    setPolicies((prev) =>
      prev.map((p) => (p.policyCode === policyCode ? { ...p, isApplied: !p.isApplied } : p)),
    );

  /** `saveSummary`는 보낸 필드만 갱신한다 — 두 textarea를 각각 저장한다 */
  const saveSummaryField = async (input: { summaryText?: string; policyHistory?: string }) => {
    if (blockReviewer(isReviewer)) return;

    setIsSaving(true);
    try {
      await edrService.saveSummary(period, company, input);
      setMessage({ type: 'success', text: '저장되었습니다.' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || '저장에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const savePolicies = async () => {
    if (blockReviewer(isReviewer)) return;

    setIsSaving(true);
    try {
      // 토글한 항목만 보내면 나머지가 "미저장"으로 남아 다음 기간에 승계 값이 섞인다
      await edrService.savePolicyStatus(
        period,
        company,
        policies.map((p) => ({ policyCode: p.policyCode, isApplied: p.isApplied })),
      );
      setMessage({ type: 'success', text: '정책 현황을 저장했습니다.' });
      await load(period, company, true);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || '정책 현황 저장에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="edr-report-admin">
      <h2>EDR 운영보고서 관리</h2>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="report-management-controls">
        <div className="control-group">
          <label htmlFor="edr-report-company">회사:</label>
          <select
            id="edr-report-company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          >
            <option value="">선택</option>
            {companies.map((c) => (
              <option key={c.orgCode} value={c.companyName}>
                {c.companyName}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="edr-report-period">기간:</label>
          <select id="edr-report-period" value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="">선택</option>
            {periods.map((p) => (
              <option key={p} value={p}>
                {formatPeriod(p)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isPeriodsLoaded && periods.length === 0 ? (
        <p className="no-data">
          조회할 수 있는 기간이 없습니다. 설치 라이선스 수집이 완료된 뒤 표시됩니다.
        </p>
      ) : !isReady ? (
        <p className="no-data">회사와 기간을 선택해주세요.</p>
      ) : (
        <>
          {/* ── 종합의견 ──────────────────────────────────── */}
          <div className="summary-section">
            <h3>요약 작성 (종합의견)</h3>
            <label htmlFor="edr-report-summary" className="edr-visually-hidden">
              종합의견
            </label>
            <textarea
              id="edr-report-summary"
              className="summary-editor"
              rows={12}
              value={summaryText}
              disabled={isLoading || isSaving}
              onChange={(e) => setSummaryText(e.target.value)}
            />
            <button
              className="btn-primary"
              onClick={() => saveSummaryField({ summaryText })}
              disabled={isLoading || isSaving}
            >
              종합의견 저장
            </button>
          </div>

          {/* ── 정책 현황 ─────────────────────────────────── */}
          <div className="policy-section">
            <h3>정책 현황</h3>
            <div className="edr-policy-toolbar">
              <span className="text-muted">총 {policies.length}건</span>
              <span className="edr-policy-toolbar__actions">
                <button className="btn-secondary" onClick={() => setAllApplied(true)}>
                  전체 적용
                </button>
                <button className="btn-secondary" onClick={() => setAllApplied(false)}>
                  전체 미적용
                </button>
              </span>
            </div>

            {POLICY_GROUPS.map(({ key, title }) => {
              const rows = policies.filter((p) => p.policyGroup === key);
              if (rows.length === 0) return null;

              return (
                <div key={key} className="edr-policy-group">
                  <h4>
                    {title} <span className="text-muted">({rows.length}건)</span>
                  </h4>
                  <table className="policy-table" aria-label={title}>
                    <thead>
                      <tr>
                        <th>정책 항목</th>
                        <th>설정 내용</th>
                        <th>출처</th>
                        <th>적용 상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.policyCode}>
                          <td>{row.policyName}</td>
                          <td className="text-muted">{row.policyDesc}</td>
                          <td>
                            {row.isSaved ? (
                              <span className="text-muted">저장됨</span>
                            ) : row.inheritedFrom ? (
                              <span className="inline-badge badge-inherited">
                                {row.inheritedFrom} 승계
                              </span>
                            ) : (
                              <span className="inline-badge badge-parse-partial">미저장</span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              aria-label={`${row.policyName} 적용 상태`}
                              aria-pressed={row.isApplied}
                              className={`inline-badge edr-policy-toggle ${
                                row.isApplied ? 'badge-active' : 'badge-inactive'
                              }`}
                              onClick={() => togglePolicy(row.policyCode)}
                            >
                              {row.isApplied ? '적용' : '미적용'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}

            <button
              className="btn-primary"
              onClick={savePolicies}
              disabled={isLoading || isSaving || policies.length === 0}
            >
              정책 현황 저장
            </button>
          </div>

          {/* ── 정책 변경 이력 ────────────────────────────── */}
          <div className="summary-section">
            <h3>정책 변경 이력</h3>
            <label htmlFor="edr-report-history" className="edr-visually-hidden">
              정책 변경 이력
            </label>
            <textarea
              id="edr-report-history"
              className="summary-editor"
              rows={7}
              value={policyHistory}
              disabled={isLoading || isSaving}
              onChange={(e) => setPolicyHistory(e.target.value)}
            />
            <button
              className="btn-primary"
              onClick={() => saveSummaryField({ policyHistory })}
              disabled={isLoading || isSaving}
            >
              정책 변경 이력 저장
            </button>
          </div>

          {/* 기존 PUBLISH 자리 — 작성완료만 기록하고, 공개는 월 승인으로 (report-approval-flow SPEC D1) */}
          <WriteCompleteBar
            query={{ pageType: 'EDR_REPORT', period, companyName: company }}
            role={isReviewer ? 'reviewer' : undefined}
          />
        </>
      )}
    </div>
  );
};

export default EdrReportAdmin;
